// src/pages/sessions/components/LiveSession/whiteboard/useWhiteboardBroadcastChannel.js
import { useCallback, useEffect, useRef, useState } from 'react';

const CHANNEL_PREFIX = 'simpleclass-whiteboard-';

/**
 * BroadcastChannel bridge for detached whiteboard communication.
 * 
 * Used by BOTH the main session tab (role='main') and the detached whiteboard tab (role='detached').
 * 
 * Main tab: owns the LiveKit connection, relays messages between LiveKit ↔ BroadcastChannel.
 * Detached tab: sends drawing events back, receives remote participant updates.
 * 
 * Message format: { source: 'main'|'detached', type: string, payload: any }
 */
export function useWhiteboardBroadcastChannel({ sessionId, role, onMessage, enabled = true }) {
  const channelRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const [peerConnected, setPeerConnected] = useState(false);

  // Keep callback ref updated without causing effect re-runs
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Initialize BroadcastChannel
  useEffect(() => {
    if (!sessionId || !enabled) return;

    const channelName = `${CHANNEL_PREFIX}${sessionId}`;
    let channel;
    
    try {
      channel = new BroadcastChannel(channelName);
    } catch (e) {
      console.error('[WhiteboardChannel] BroadcastChannel not supported:', e);
      return;
    }
    
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const { source, type, payload } = event.data || {};

      // Ignore messages from our own role (echo suppression)
      if (source === role) return;

      // Peer detection handshake
      if (type === 'ping') {
        channel.postMessage({ source: role, type: 'pong' });
        setPeerConnected(true);
        return;
      }

      if (type === 'pong') {
        setPeerConnected(true);
        return;
      }

      if (type === 'detached_closed') {
        setPeerConnected(false);
        return;
      }

      if (type === 'detached_closing') {
        setPeerConnected(false);
        // Also forward to application handler so it can reset state
        if (onMessageRef.current) {
          onMessageRef.current(type, payload);
        }
        return;
      }

      // Forward all other messages to the handler
      if (onMessageRef.current) {
        onMessageRef.current(type, payload);
      }
    };

    // Announce our presence — the other tab (if open) will respond with 'pong'
    channel.postMessage({ source: role, type: 'ping' });

    return () => {
      // Notify peer we're leaving
      try {
        channel.postMessage({ source: role, type: 'detached_closed' });
      } catch (e) {
        // Channel may already be closed
      }
      channel.close();
      channelRef.current = null;
      setPeerConnected(false);
    };
  }, [sessionId, role, enabled]);

  // Send a typed message to the peer tab
  const sendMessage = useCallback((type, payload) => {
    if (channelRef.current) {
      try {
        channelRef.current.postMessage({ source: role, type, payload });
      } catch (e) {
        console.error('[WhiteboardChannel] Failed to send:', e);
      }
    }
  }, [role]);

  return { sendMessage, peerConnected };
}
